import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaCogs, FaGift } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import DefaultInput from '~/components/Inputs/Default';
import CurrencyInput from '~/components/Inputs/Currency';
import Toggle from '~/components/Toggle';
import SaveButton from '~/components/Buttons/Save';
import ExitButton from '~/components/Buttons/Exit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';

import { getCurrencyFormatValue } from '~/utils';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import {
  Container,
  Content,
  OneFourthWrapper,
  HalfWrapper,
  ButtonContainer,
} from './styles';

interface IFormGiftType {
  name: string;
  erp_code: string;
  active: boolean;
  price: number;
}

interface ILocation {
  id: string;
  name: string;
  erp_code: string;
  active: boolean;
  price: number;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, control, errors } = useForm<IFormGiftType>({
    resolver: yupResolver(schema),
    defaultValues: {
      active: state?.active,
    },
  });

  const submitForm = useCallback(
    async (data: IFormGiftType) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/configurations/gifttypes', {
            ...data,
            price: getCurrencyFormatValue(data.price),
          });

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Tipo de brinde cadastrado com sucesso.',
          });
        } else {
          await api.put(`/configurations/gifttypes`, {
            id: state.id,
            ...data,
            price: getCurrencyFormatValue(data.price),
          });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Tipo de brinde atualizado com sucesso.',
          });
        }

        history.push('/configurations/gifttypes');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  const onExit = useCallback(() => {
    history.push('/configurations/gifttypes');
  }, [history]);

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/gifttypes"
        firstChild="Tipos de Brindes"
        secondChild={
          state === undefined
            ? 'Novo Tipo de Brinde'
            : 'Edição de Tipo de Brinde'
        }
        mainIcon={FaCogs}
        firstChildIcon={FaGift}
        secondChildIcon={FaGift}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <Content style={{ maxWidth: 700 }}>
          <OneFourthWrapper>
            <DefaultInput
              name="erp_code"
              labelFor="erp_code"
              labelText="Código ERP"
              type="text"
              defaultValue={state?.erp_code}
              error={errors?.erp_code?.message}
              ref={register}
              maxLength={10}
            />
          </OneFourthWrapper>

          <HalfWrapper>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </HalfWrapper>

          <OneFourthWrapper>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </OneFourthWrapper>
        </Content>

        <Content style={{ maxWidth: 700 }}>
          <OneFourthWrapper>
            <CurrencyInput
              name="price"
              labelFor="price"
              labelText="Valor"
              defaultValue={state?.price || 0}
              error={errors?.price?.message}
              ref={register}
            />
          </OneFourthWrapper>
        </Content>

        <ButtonContainer>
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </ButtonContainer>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação do tipo de brinde?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
